import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { get, getText, post } from './hooks/apiFetch';
import { SupportModal } from '../helpers';
import { AI_UTILITIES } from '../constants/aiUtilities';
import MarkdownPreviewModal from '../helpers/MarkdownPreviewModal/MarkdownPreviewModal';
import './Footer.css';
import { useNotification } from './Notification/NotificationContext';
import { useAuth } from './Auth/AuthContext';

export const Footer = () => {
    const { isAuthenticated } = useAuth();
    const [versionApp, setVersionApp] = useState('');
    const [modal, setModal] = useState(false);
    const [message, setMessage] = useState('');
    const [messageMaxLength] = useState(10000);
    const [supportSubject, setSupportSubject] = useState('');
    const [customSubject, setCustomSubject] = useState('');
    const [customSubjectMaxLength] = useState(100);
    const [senderNameMaxLength] = useState(100);
    const [senderEmailMaxLength] = useState(100);
    const [files, setFiles] = useState([]);
    const [totalFileSize, setTotalFileSize] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [manualUrl, setManualUrl] = useState('');
    const [manualTitle, setManualTitle] = useState('');
    const [lastReleaseNotes, setLastReleaseNotes] = useState(null);
    const [showReleaseNotesModal, setShowReleaseNotesModal] = useState(false);
    const [userViewedVersion, setUserViewedVersion] = useState(null);
    const [hasCheckedReleaseNotes, setHasCheckedReleaseNotes] = useState(false);
    const {
        showSuccess,
        showWarning,
        showError
    } = useNotification();

    const getTotalMessageSize = useCallback(() => {
        const textSizeBytes = new Blob([message || '']).size;
        const filesSizeBytes = files.reduce((sum, file) => sum + file.size, 0);
        return textSizeBytes + filesSizeBytes;
    }, [message, files]);

    const fetchLastReleaseNotes = useCallback(async () => {
        try {
            const data = await get('/api/app/LastReleaseNotes');
            setLastReleaseNotes(data);
            if (data && data.userViewedVersion !== undefined) {
                setUserViewedVersion(data.userViewedVersion);
            }
        } catch (error) {
            console.error(error);
            setLastReleaseNotes(null);
        }
    }, []);

    const loadAppVersion = useCallback(async () => {
        try {
            const version = await getText('/api/app/version');
            setVersionApp(version);
        } catch (error) {
            console.error(error);
            setVersionApp('');
        }
    }, []);

    const loadManualData = useCallback(async () => {
        try {
            const manualData = await get('/api/manual');
            setManualUrl(manualData.url || '');
            setManualTitle(manualData.manualTitle || 'Открыть инструкцию');
        } catch (error) {
            console.error(error);
            setManualUrl('');
            setManualTitle('Инструкция недоступна');
        }
    }, []);

    useEffect(() => {
        loadAppVersion();
        if (isAuthenticated) {
            loadManualData();
            fetchLastReleaseNotes();
        }
    }, [loadAppVersion, loadManualData, fetchLastReleaseNotes, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && lastReleaseNotes && !hasCheckedReleaseNotes) {
            const viewed = lastReleaseNotes.userViewedVersion;
            if (!viewed || viewed !== lastReleaseNotes.version) {
                setShowReleaseNotesModal(true);
            }
            setHasCheckedReleaseNotes(true);
        }
    }, [isAuthenticated, lastReleaseNotes, hasCheckedReleaseNotes]);

    const downloadManual = async () => {
        if (!manualUrl) return;
        try {
            window.open(manualUrl, '_blank');
        } catch (error) {
            console.error(error);
            showError('Ошибка при скачивании файла');
        }
    };

    const toggleModal = () => {
        const path = window.location.pathname;
        let subject = 'Интеллектуальная утилита ';

        if (path.startsWith(AI_UTILITIES.TRANSCRIBE.path)) {
            subject += `"${AI_UTILITIES.TRANSCRIBE.name}"`;
        } else if (path.startsWith(AI_UTILITIES.DIARIZE.path)) {
            subject += `"${AI_UTILITIES.DIARIZE.name}"`;
        } else if (path.startsWith(AI_UTILITIES.SUMMARIZE_RESUME.path)) {
            subject += `"${AI_UTILITIES.SUMMARIZE_RESUME.name}"`;
        } else if (path.startsWith(AI_UTILITIES.UNIVERSAL_SUMMARIZE.path)) {
            subject += `"${AI_UTILITIES.UNIVERSAL_SUMMARIZE.name}"`;
        } else if (path.startsWith(AI_UTILITIES.DEPERSONALIZE.path)) {
            subject += `"${AI_UTILITIES.DEPERSONALIZE.name}"`;
        } else if (path.startsWith(AI_UTILITIES.RETELLING.path)) {
            subject += `"${AI_UTILITIES.RETELLING.name}"`;
        } else if (path.startsWith(AI_UTILITIES.STANDARD_CONTROL_PSA.path)) {
            subject += `"${AI_UTILITIES.STANDARD_CONTROL_PSA.name}"`;
        } else if (path.startsWith(AI_UTILITIES.MARKET_PRICE_ANALYSIS.path)) {
            subject += `"${AI_UTILITIES.MARKET_PRICE_ANALYSIS.name}"`;
        } else if (path.startsWith(AI_UTILITIES.INVOICE_RECOGNIZER.path)) {
            subject += `"${AI_UTILITIES.INVOICE_RECOGNIZER.name}"`;
        } else {
            subject = 'Интеллектуальные утилиты';
        }

        setModal(prev => !prev);
        setMessage('');
        setCustomSubject('');
        setIsLoading(false);
        setSupportSubject(subject);
        setFiles([]);
        setTotalFileSize(0);
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);

        let updatedSize = totalFileSize;
        const validFiles = [];

        for (const file of newFiles) {
            if (updatedSize + file.size <= 10 * 1024 * 1024) {
                validFiles.push(file);
                updatedSize += file.size;
            }
        }

        setFiles(prev => [...prev, ...validFiles]);
        setTotalFileSize(updatedSize);
    };

    const handleRemoveFile = (index) => {
        const fileToRemove = files[index];
        setFiles(prev => prev.filter((_, i) => i !== index));
        setTotalFileSize(prev => prev - fileToRemove.size);
    };

    const handleClearFiles = () => {
        setFiles([]);
        setTotalFileSize(0);
    };

    const handleCancel = () => {
        setModal(false);
        setMessage('');
        setCustomSubject('');
        setIsLoading(false);
        setFiles([]);
        setTotalFileSize(0);
    };

    const handleSubmit = async (e, senderName = null, senderEmail = null) => {
        e.preventDefault();

        if (!message.trim()) {
            showWarning('Письмо должно содержать текст');
            return;
        }

        if (senderName?.length > senderNameMaxLength) {
            showWarning(`Превышен лимит символов в ФИО`);
            return;
        }

        if (senderEmail?.length > senderEmailMaxLength) {
            showWarning(`Превышен лимит символов в email`);
            return;
        }

        if (customSubject.length > customSubjectMaxLength) {
            showWarning('Превышен лимит символов в теме');
            return;
        }

        if (message.length > messageMaxLength) {
            showWarning('Превышен лимит текста');
            return;
        }

        if (getTotalMessageSize() > 10 * 1024 * 1024) {
            showWarning('Превышен общий размер 10MB');
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append(
                'Subject',
                (customSubject.trim()
                    ? `${supportSubject} - ${customSubject}`
                    : supportSubject
                ).trim()
            );

            formData.append('Message', message.trim());

            if (senderName) formData.append('SenderFullName', senderName);
            if (senderEmail) formData.append('SenderEmail', senderEmail);

            files.forEach(f => formData.append('Files', f));

            await post('/api/Email/SendSupportMessage', formData);

            handleCancel();
            showSuccess('Письмо успешно отправлено');
        } catch (error) {
            const msg =
                error?.name === 'TypeError' || error?.message === 'Failed to fetch'
                    ? 'Нет соединения с сервером'
                    : error?.message || 'Ошибка отправки';

            showError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseReleaseNotes = useCallback(async () => {
        if (lastReleaseNotes && userViewedVersion !== lastReleaseNotes.version) {
            try {
                await post('/api/app/ViewedReleaseNotes', lastReleaseNotes.version);
                setUserViewedVersion(lastReleaseNotes.version);
            } catch (error) {
                console.error('Failed to save viewed release notes', error.message);
            }
        }
        setShowReleaseNotesModal(false);
    }, [lastReleaseNotes, userViewedVersion]);

    const openReleaseNotesModal = useCallback(() => {
        if (lastReleaseNotes) {
            setShowReleaseNotesModal(true);
        } else {
            showWarning('Нет доступных Release Notes');
        }
    }, [lastReleaseNotes, showWarning]);

    return (
        <footer>
            <Container className="mt-5">
                <Row>
                    <Col>
                        <Row className="justify-content-center text-center mb-2">
                            {lastReleaseNotes && (
                                <Button color="link" onClick={openReleaseNotesModal} className="p-0">
                                    Что нового?
                                </Button>
                            )}
                        </Row>
                        <Row className="justify-content-center text-center mb-2">
                            {manualUrl && (
                                <Button color="link" onClick={downloadManual} className="p-0">
                                    {manualTitle}
                                </Button>
                            )}
                        </Row>
                        <Row className="justify-content-center text-center mb-3">
                            <Button color="link" onClick={toggleModal} className="p-0">
                                Написать обращение в техподдержку
                            </Button>
                        </Row>
                        <Row className="justify-content-center text-center developed-by">
                            ООО "Цифровые технологии 1520" (с) 2025—2026
                        </Row>
                        <Row className="justify-content-center text-center">
                            <span className="version">Версия: {versionApp}</span>
                        </Row>
                    </Col>
                </Row>
            </Container>

            <SupportModal
                isOpen={modal}
                toggle={toggleModal}
                senderNameMaxLength={senderNameMaxLength}
                senderEmailMaxLength={senderEmailMaxLength}
                subject={supportSubject}
                customSubject={customSubject}
                customSubjectMaxLength={customSubjectMaxLength}
                onSubjectChange={(e) => setCustomSubject(e.target.value)}
                message={message}
                messageMaxLength={messageMaxLength}
                onMessageChange={(e) => setMessage(e.target.value)}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isLoading={isLoading}
                files={files}
                totalMessageSize={getTotalMessageSize()}
                totalFileSize={totalFileSize}
                maxTotalSize={9.5 * 1024 * 1024}
                onFileChange={handleFileChange}
                onRemoveFile={handleRemoveFile}
                onClearFiles={handleClearFiles}
            />

            <MarkdownPreviewModal
                isOpen={showReleaseNotesModal}
                toggle={handleCloseReleaseNotes}
                title={`Release Notes ${lastReleaseNotes?.version || ''}`}
                content={lastReleaseNotes?.content || ''}
            />
        </footer>
    );
};